import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import TopBar from "@/components/TopBar";
import LocationPicker from "@/components/LocationPicker";
import CustomDatePicker from "@/components/DateTimePicker";
import ModalTimePicker from "@/components/ModalTimePicker";
import PhoneInput from "@/components/PhoneInput";
import MultiPhoneInput, { serializePhones, validatePhonesList } from "@/components/MultiPhoneInput";
import { cityLookupValue } from "@/utils/locationParser";
import api, { formatApiError } from "@/lib/api";
import { fmtNum, fmtRiyal } from "@/utils/toEnglishDigits";
import NumberField from "@/components/NumberField";
import { combineDateTime } from "@/utils/date";
import {
  ArrowLeft,
  ArrowsHorizontal,
  CalendarBlank,
  Clock,
  MapPin,
  Minus,
  Plus,
  Trash,
  UploadSimple,
  User as UserIcon,
  Users,
  X,
  Car as CarIcon,
  Airplane,
} from "@phosphor-icons/react";

const AR_WEEKDAY = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
const arWeekday = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return AR_WEEKDAY[d.getDay()];
};

const emptyRoute = () => {
  const now = new Date();
  // 2026-07 §MinuteStep5: round UP to the next 5-min boundary so the
  // default trip_time aligns with the picker's 5-min grid.
  const roundedMin = Math.ceil(now.getMinutes() / 5) * 5;
  if (roundedMin >= 60) {
    now.setHours(now.getHours() + 1, 0, 0, 0);
  } else {
    now.setMinutes(roundedMin, 0, 0);
  }
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mi = String(now.getMinutes()).padStart(2, "0");
  return {
    pickup_location: "",
    destination: "",
    trip_date: `${yyyy}-${mm}-${dd}`,
    trip_time: `${hh}:${mi}`,
    flight_number: "",
    flight_airline: "",
    cars: {}, // { car_type: qty }
    ticket_image: null,
  };
};

const useDebounced = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const SectionCard = React.forwardRef(function SectionCard({ children, className = "", ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={`rounded-2xl border border-primary/25 bg-gradient-to-br from-[#0F2148] to-[#172956] p-4 md:p-5 ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
});

function QtyBox({ value, onChange, disabled, testId }) {
  return (
    <div dir="ltr" className="inline-flex items-center gap-1 select-none">
      <button
        type="button"
        onClick={() => !disabled && onChange(Math.max(0, value - 1))}
        disabled={disabled || value <= 0}
        data-testid={`${testId}-minus`}
        className="w-8 h-8 rounded-full border border-primary/40 text-primary flex items-center justify-center hover:bg-primary/10 disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <Minus size={14} weight="bold" />
      </button>
      <span
        className="w-9 text-center font-bold text-primary num text-lg"
        data-testid={`${testId}-value`}
      >
        {fmtNum(value)}
      </span>
      <button
        type="button"
        onClick={() => !disabled && onChange(value + 1)}
        disabled={disabled}
        data-testid={`${testId}-plus`}
        className="w-8 h-8 rounded-full border border-primary/40 text-primary flex items-center justify-center hover:bg-primary/10 disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <Plus size={14} weight="bold" />
      </button>
    </div>
  );
}

// Card that lists cars for a route with price + qty selector.
// Always renders cars: uses per-route prices if pickup+destination filled,
// otherwise falls back to a plain list of all fleet cars with qty pickers (no price).
function CarPicker({ routeIndex, route, onQtyChange, prices, loading, fallbackCars, t }) {
  const hasRoute = !!(route.pickup_location && route.destination);

  if (hasRoute && loading) {
    return (
      <div className="text-primary text-sm text-center py-6 animate-pulse">
        {t("newBooking.quoteLoading")}
      </div>
    );
  }

  // When no route yet, or when no prices for the route, show fleet fallback so
  // users can still pick qty. Price shows "—" until pickup+destination filled.
  const list = hasRoute && prices && prices.length > 0
    ? prices
    : (fallbackCars || []).map((c) => ({
        car_type: c.id,
        name_ar: c.name_ar,
        capacity: c.capacity,
        image_url: c.image_url,
        has_price: false,
        price: 0,
      }));

  if (list.length === 0) {
    return (
      <div className="text-white/50 text-sm text-center py-6 border border-dashed border-primary/25 rounded-xl">
        {t("newBooking.noCarsAvailable")}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {list.map((p) => {
        const qty = Number(route.cars[p.car_type] || 0);
        const active = qty > 0;
        // Allow selecting even without price (unlimited quantities)
        const disabled = false;
        return (
          <div
            key={p.car_type}
            data-testid={`car-row-${routeIndex}-${p.car_type}`}
            className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
              active
                ? "border-primary bg-primary/10 shadow-[0_0_0_1px_var(--primary)]"
                : "border-primary/20 bg-[#0B1A3A]/60"
            }`}
          >
            {p.image_url && String(p.image_url).length > 400 ? (
              <img
                src={p.image_url}
                alt={p.name_ar}
                className="w-16 h-12 object-cover rounded-lg border border-primary/20 shrink-0"
              />
            ) : (
              <div className="w-16 h-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <CarIcon size={22} weight="duotone" className="text-primary/70" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold text-sm truncate">{p.name_ar}</div>
              <div className="text-white/60 text-[11px] mt-0.5 flex items-center gap-1 flex-wrap">
                <span className="num">{fmtNum(p.capacity)}</span> {t("newBooking.carPax")}
                {hasRoute && p.has_price ? (
                  <>
                    <span>·</span>
                    <span className="text-primary font-semibold num">{fmtRiyal(p.price)}</span>
                    <span className="text-white/50">{t("newBooking.carUnit")}</span>
                  </>
                ) : hasRoute && !p.has_price ? (
                  <>
                    <span>·</span>
                    <span className="text-[#F87171] text-[10px]">{t("newBooking.carNotAvailable")}</span>
                  </>
                ) : (
                  <>
                    <span>·</span>
                    <span className="text-white/40 text-[10px]">{t("newBooking.selectRoutePrompt")}</span>
                  </>
                )}
              </div>
            </div>
            <QtyBox
              value={qty}
              onChange={(v) => onQtyChange(p.car_type, v)}
              disabled={disabled}
              testId={`car-qty-${routeIndex}-${p.car_type}`}
            />
          </div>
        );
      })}
    </div>
  );
}

export default function NewBooking() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCarId = searchParams.get("carId") || "";
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  void user;

  const [routes, setRoutes] = useState([emptyRoute()]);
  const [client, setClient] = useState({
    client_name: "",
    client_phones: [{ number: "+966", is_primary: true }],
    customer_email: "",
    company_name: "",
  });
  const [passengers, setPassengers] = useState(1);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [fleetCars, setFleetCars] = useState([]);

  // ── Admin-only "on behalf of" company picker ─────────────────────
  const isAdmin = !!(user?.is_admin || user?.role === "admin");
  const [approvedCompanies, setApprovedCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(null); // id when picked from list
  const [companyFocused, setCompanyFocused] = useState(false);
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    api.get("/admin/companies?status=approved")
      .then((r) => {
        if (cancelled) return;
        const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        // Sort alphabetically (Arabic) once at load time so autocomplete
        // filtering preserves order.
        arr.sort((a, b) =>
          (a.company_name || a.full_name || a.name || "").toString()
            .localeCompare(
              (b.company_name || b.full_name || b.name || "").toString(),
              "ar",
              { sensitivity: "base", numeric: true },
            ),
        );
        setApprovedCompanies(arr);
      })
      .catch(() => setApprovedCompanies([]));
    return () => { cancelled = true; };
  }, [isAdmin]);

  const companySuggestions = useMemo(() => {
    if (!isAdmin) return [];
    const q = String(client.company_name || "").trim().toLowerCase();
    const list = approvedCompanies.filter(
      (c) => (c.company_name || c.name || "").toString().trim() !== "",
    );
    // Filter on query, but return ALL matches (no hard-coded slice) — the
    // dropdown itself has an internal scroll bar for overflow.
    if (!q) return list;
    return list.filter((c) =>
      (c.company_name || c.name || "").toString().toLowerCase().includes(q),
    );
  }, [approvedCompanies, client.company_name, isAdmin]);

  // Preselect car via ?carId= (from /car/:id "احجز هذه السيارة" button).
  useEffect(() => {
    if (!preselectedCarId) return;
    setRoutes((prev) => {
      const next = [...prev];
      const first = { ...(next[0] || emptyRoute()) };
      first.cars = { ...(first.cars || {}), [preselectedCarId]: Math.max(1, first.cars?.[preselectedCarId] || 1) };
      next[0] = first;
      return next;
    });
  }, [preselectedCarId]);

  // Load fleet cars so we can render the always-visible car list before a route is chosen
  // 2026-02-28 §VisibleCars — re-fetch when the admin picks a different
  // company so cars hidden for that company disappear from the picker.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/cars", {
          params: isAdmin && companyId ? { company_id: companyId } : {},
        });
        if (!cancelled) setFleetCars(Array.isArray(data) ? data : []);
      } catch {
        /* ignore — car picker will handle empty */
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, isAdmin]);

  // Route-level prices cache keyed by "pickup|destination|date"
  const [routePricesMap, setRoutePricesMap] = useState({});
  const [pricesLoading, setPricesLoading] = useState({}); // per route index

  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);

  const updateRoute = (i, patch) => {
    setRoutes((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  // Refs for auto-scrolling to the newly added route.
  const routeRefs = useRef({});
  const addRoute = () => {
    setRoutes((r) => {
      const next = [...r, emptyRoute()];
      // After React commits the new DOM, scroll the just-added card into view.
      setTimeout(() => {
        const el = routeRefs.current[next.length - 1];
        if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
      return next;
    });
  };
  const removeRoute = (i) =>
    setRoutes((r) => (r.length <= 1 ? r : r.filter((_, idx) => idx !== i)));

  const reverseRoute = (i) => {
    if (i <= 0) return;
    const prev = routes[i - 1];
    updateRoute(i, {
      pickup_location: prev.destination || "",
      destination: prev.pickup_location || "",
    });
  };

  // Fetch route-prices per unique route whenever pickup/destination/date change.
  // NOTE: route_rules match on the CITY portion only (e.g., "مكة المكرمة"),
  // not the full combined address. cityLookupValue() extracts the city so
  // shrines / tour keep their full labels while regular cities strip the detail.
  // 2026-02-28 §CustomCompanyPricing: also scope by the admin's selected
  // "on behalf of" company so per-company custom prices show up in the
  // per-car price chips exactly like the final booking will be priced.
  const debouncedRoutes = useDebounced(routes, 400);
  useEffect(() => {
    debouncedRoutes.forEach(async (r, idx) => {
      const pickCity = cityLookupValue(r.pickup_location);
      const destCity = cityLookupValue(r.destination);
      const key = `${pickCity}|${destCity}|${r.trip_date}|${companyId || ""}`;
      if (!pickCity || !destCity) return;
      if (routePricesMap[key]) return; // cached
      setPricesLoading((p) => ({ ...p, [idx]: true }));
      try {
        const { data } = await api.get("/route-prices", {
          params: {
            pickup: pickCity,
            destination: destCity,
            date: r.trip_date || "",
            ...(isAdmin && companyId ? { company_id: companyId } : {}),
          },
        });
        setRoutePricesMap((m) => ({ ...m, [key]: data.prices || [] }));
      } catch {
        setRoutePricesMap((m) => ({ ...m, [key]: [] }));
      } finally {
        setPricesLoading((p) => ({ ...p, [idx]: false }));
      }
    });
  }, [debouncedRoutes, companyId, isAdmin]);

  const getRoutePrices = (r) => {
    const pickCity = cityLookupValue(r.pickup_location);
    const destCity = cityLookupValue(r.destination);
    return routePricesMap[`${pickCity}|${destCity}|${r.trip_date}|${companyId || ""}`] || [];
  };

  // Auto-quote — recomputes total when any cars-selection changes.
  const carSig = useMemo(
    () =>
      routes.map((r) => `${r.pickup_location}|${r.destination}|${r.trip_date}|${JSON.stringify(r.cars)}`).join("::") + `::${companyId || ""}`,
    [routes, companyId],
  );
  const debouncedSig = useDebounced(carSig, 400);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const anyPick = routes.some(
        (r) => r.pickup_location && r.destination && Object.values(r.cars).some((q) => Number(q) > 0),
      );
      if (!anyPick) {
        setQuote(null);
        return;
      }
      setQuoting(true);
      try {
        const body = {
          // 2026-02-28 §CustomCompanyPricing: admin quotes on behalf of a
          // selected company need this so the backend applies its custom
          // per-company prices instead of the general route_rules.
          ...(isAdmin && companyId ? { company_id: companyId } : {}),
          routes: routes.map((r) => ({
            // Use city-only pattern for backend matching; the combined address
            // is kept in the booking payload on submit for storage/display.
            pickup_location: cityLookupValue(r.pickup_location),
            destination: cityLookupValue(r.destination),
            date: r.trip_date,
            cars: Object.entries(r.cars)
              .filter(([, q]) => Number(q) > 0)
              .map(([car_type, quantity]) => ({ car_type, quantity: Number(quantity) })),
          })),
        };
        const { data } = await api.post("/bookings/quote", body);
        if (!cancelled) setQuote(data);
      } catch {
        if (!cancelled) setQuote(null);
      } finally {
        if (!cancelled) setQuoting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSig]);

  const onTicketChange = (i, e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error(t("newBooking.err_size"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => updateRoute(i, { ticket_image: reader.result });
    reader.readAsDataURL(file);
  };

  const validate = () => {
    if (!client.client_name.trim()) return t("newBooking.err_client_name");
    const phoneError = validatePhonesList(client.client_phones);
    if (phoneError) return phoneError;
    const phones = serializePhones(client.client_phones);
    if (phones.length === 0) return t("newBooking.err_phones");
    for (let i = 0; i < routes.length; i++) {
      const r = routes[i];
      if (!r.trip_date || !r.trip_time) return t("newBooking.err_date_time", { n: i + 1 });
      if (!r.pickup_location || !r.destination) return t("newBooking.err_pickup_dest", { n: i + 1 });
      const hasCar = Object.values(r.cars).some((q) => Number(q) > 0);
      if (!hasCar) return t("newBooking.err_car", { n: i + 1 });
    }
    if (Number(passengers) < 1) return t("newBooking.err_pax_required");
    if (totalCapacity > 0 && Number(passengers) > totalCapacity) {
      return t("newBooking.err_pax_capacity", { pax: passengers, cap: totalCapacity });
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      // 2026-02 §OneBookingMultiRoute: send the WHOLE trip as a single POST
      // with `routes[]` — no more splitting into N sibling bookings per
      // (route × car × qty). Server prices every route from route_rules.
      const clientPhonesSerialised = serializePhones(client.client_phones);
      const primaryClientPhone = clientPhonesSerialised[0]?.number || null;
      const secondaryClientPhone = clientPhonesSerialised.find((p) => !p.is_primary)?.number || null;

      const payloadRoutes = routes.map((r) => {
        const carsArr = Object.entries(r.cars || {})
          .map(([car_type, qty]) => ({ car_type, quantity: Number(qty) || 0 }))
          .filter((c) => c.quantity > 0);
        const _isTrain =
          (r.pickup_location && String(r.pickup_location).includes("محطة قطار")) ||
          (r.destination && String(r.destination).includes("محطة قطار"));
        return {
          pickup_location: r.pickup_location,
          destination: r.destination,
          trip_date: r.trip_date,
          trip_time: r.trip_time,
          passengers: Number(passengers) || 1,
          flight_number: _isTrain ? null : (r.flight_number || null),
          flight_airline: _isTrain ? "قطار" : (r.flight_airline || null),
          ticket_image: r.ticket_image || null,
          cars: carsArr,
        };
      });

      // First route + first car mirror onto the flat fields for legacy readers.
      const first = payloadRoutes[0] || {};
      const firstCar = (first.cars || [{}])[0];
      const iso = combineDateTime(first.trip_date, first.trip_time);

      const payload = {
        client_name: client.client_name,
        client_phones: clientPhonesSerialised,
        client_phone: primaryClientPhone,
        client_phone_alt: secondaryClientPhone,
        customer_email: client.customer_email || undefined,
        company_id: undefined,
        company_name: (client.company_name || "").trim() || undefined,
        on_behalf_of_user_id: isAdmin && companyId ? companyId : undefined,
        company_name_manual:
          isAdmin && !companyId && (client.company_name || "").trim()
            ? client.company_name.trim()
            : undefined,
        pickup_time: iso,
        trip_date: first.trip_date,
        trip_time: first.trip_time,
        pickup_location: first.pickup_location,
        destination: first.destination,
        car_type: firstCar?.car_type || null,
        passengers: Number(passengers) || 1,
        notes,
        flight_number: first.flight_number,
        flight_airline: first.flight_airline,
        ticket_image: first.ticket_image,
        // 2026-02 §StatusFlow: new bookings start as "pending" — admin will
        // confirm/reject from the panel.
        status: "pending",
        routes: payloadRoutes,
      };
      const { data } = await api.post("/bookings", payload);
      toast.success(t("newBooking.created", { n: fmtNum(1) }));
      navigate("/company-bookings");
    } catch (e2) {
      toast.error(formatApiError(e2) || t("newBooking.err_create"));
    } finally {
      setSaving(false);
    }
  };

  const totalCarsCount = routes.reduce(
    (sum, r) => sum + Object.values(r.cars).reduce((s, q) => s + Number(q || 0), 0),
    0,
  );
  const totalCapacity = routes.reduce((sum, r) => {
    const prices = getRoutePrices(r);
    return (
      sum +
      Object.entries(r.cars).reduce((s, [ct, q]) => {
        const p = prices.find((pp) => pp.car_type === ct);
        return s + Number(p?.capacity || 0) * Number(q || 0);
      }, 0)
    );
  }, 0);

  return (
    <div className="min-h-screen bg-background text-foreground" dir={isEn ? "ltr" : "rtl"}>
      <TopBar />
      <main className="px-4 py-6" data-testid="new-booking-page">
        <div className="max-w-3xl mx-auto space-y-4">
          {/* 1) Header */}
          <div className="flex items-center gap-3 pb-1">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-full border border-primary/40 text-primary hover:bg-primary/10 flex items-center justify-center"
              aria-label={t("newBooking.back")}
              data-testid="btn-back"
            >
              <ArrowLeft size={16} weight="bold" className={isEn ? "rotate-180" : ""} />
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex-1 text-center">{t("newBooking.title")}</h1>
            <span className="w-9" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {/* 2) Client card */}
            <SectionCard data-testid="section-client">
              <div className="flex items-center gap-2 mb-3">
                <UserIcon size={18} weight="duotone" className="text-primary" />
                <h2 className="text-white font-bold text-lg">{t("newBooking.clientSection")}</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* الشركة — بحث ذكي في الشركات المعتمدة (للأدمن فقط) */}
                {isAdmin && (
                  <div className="md:col-span-2 relative">
                    <label className="block text-xs text-white/60 mb-1">
                      {t("newBooking.companyLabel")}
                    </label>
                    <input
                      type="text"
                      value={client.company_name}
                      onChange={(e) => {
                        // Any manual edit clears the linked company id.
                        setClient({ ...client, company_name: e.target.value });
                        if (companyId) setCompanyId(null);
                      }}
                      onFocus={() => setCompanyFocused(true)}
                      onBlur={() => setTimeout(() => setCompanyFocused(false), 200)}
                      className="mz-input"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder={t("newBooking.companySearchPh")}
                      data-testid="company-name"
                    />
                    {companyFocused && companySuggestions.length > 0 && (
                      <div
                        data-testid="company-dropdown"
                        className="absolute z-30 mt-1 w-full rounded-xl border border-primary/30 bg-[#0F1F42] shadow-2xl flex flex-col overflow-hidden"
                        style={{ maxHeight: 320 }}
                      >
                        <div
                          className="flex-1 overflow-y-auto min-h-0"
                          data-testid="company-dropdown-list"
                        >
                          {companySuggestions.map((c) => {
                            const label = c.company_name || c.name || c.email;
                            return (
                              <button
                                key={c.id}
                                type="button"
                                data-testid={`company-pick-${c.id}`}
                                onMouseDown={(e) => {
                                  // Use onMouseDown so onBlur doesn't fire first.
                                  e.preventDefault();
                                  setClient({ ...client, company_name: label });
                                  setCompanyId(c.id);
                                  setCompanyFocused(false);
                                }}
                                className={`w-full ${isEn ? "text-left" : "text-right"} px-3 py-2 text-sm text-white/90 hover:bg-primary/15 border-b border-white/5 last:border-b-0 flex items-center gap-2`}
                              >
                                <span className="text-primary">🏢</span>
                                <span className="flex-1 truncate">{label}</span>
                                {c.email && (
                                  <span className="text-[10px] text-white/40 truncate" dir="ltr">
                                    {c.email}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        <div
                          className="shrink-0 px-3 py-1.5 text-[10px] text-white/40 text-center border-t border-white/10 bg-black/20"
                          data-testid="company-dropdown-footer"
                        >
                          <span className="num">{fmtNum(companySuggestions.length)}</span> {t("newBooking.companyCount")}
                          {companySuggestions.length > 6 && t("newBooking.companyScrollMore")}
                        </div>
                      </div>
                    )}
                    {/* Status badge */}
                    {companyId ? (
                      <div
                        data-testid="company-badge-linked"
                        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-300"
                      >
                        {t("newBooking.companyLinked")}
                      </div>
                    ) : (client.company_name || "").trim() ? (
                      <div
                        data-testid="company-badge-manual"
                        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-300"
                      >
                        {t("newBooking.companyManual")}
                      </div>
                    ) : null}
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="block text-xs text-white/60 mb-1">{t("newBooking.clientName")}</label>
                  <input
                    type="text"
                    value={client.client_name}
                    onChange={(e) => setClient({ ...client, client_name: e.target.value })}
                    className="mz-input"
                    required
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder={t("newBooking.clientNamePh")}
                    data-testid="client-name"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-white/60 mb-1">{t("newBooking.phones")}</label>
                  <MultiPhoneInput
                    value={client.client_phones}
                    onChange={(v) => setClient({ ...client, client_phones: v })}
                    testIdPrefix="client-phones"
                    required
                    maxEntries={5}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-white/60 mb-1">
                    {t("newBooking.email")}
                  </label>
                  <input
                    type="email"
                    value={client.customer_email}
                    onChange={(e) => setClient({ ...client, customer_email: e.target.value })}
                    placeholder="example@email.com"
                    autoComplete="email"
                    data-testid="client-email"
                    className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 focus:border-primary text-white text-sm outline-none transition-colors"
                    dir="ltr"
                  />
                </div>
              </div>
            </SectionCard>

            {/* 3) Routes */}
            <div className="pt-1">
              <h2 className="text-white font-bold text-lg mb-2">{t("newBooking.tripSection")}</h2>
            </div>
            {routes.map((r, i) => {
              const prices = getRoutePrices(r);
              const routeTotal =
                (quote?.routes?.[i]?.route_total ??
                  Object.entries(r.cars).reduce((s, [ct, q]) => {
                    const p = prices.find((pp) => pp.car_type === ct);
                    return s + Number(p?.price || 0) * Number(q || 0);
                  }, 0)) || 0;

              return (
                <SectionCard
                  key={i}
                  data-testid={`route-card-${i}`}
                  ref={(el) => { routeRefs.current[i] = el; }}
                >
                  {/* Route header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center num">
                        {fmtNum(i + 1)}
                      </span>
                      <span className="text-white font-bold">{t("newBooking.route")} {fmtNum(i + 1)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {i > 0 && (
                        <button
                          type="button"
                          onClick={() => reverseRoute(i)}
                          data-testid={`btn-reverse-${i}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-dashed border-primary/60 bg-primary/10 text-primary text-xs hover:bg-primary/20"
                        >
                          <ArrowsHorizontal size={13} /> {t("newBooking.reverse")}
                        </button>
                      )}
                      {routes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRoute(i)}
                          data-testid={`btn-remove-route-${i}`}
                          className="w-8 h-8 rounded-full border border-destructive/40 text-destructive hover:bg-destructive/10 flex items-center justify-center"
                          aria-label={t("newBooking.removeRoute")}
                        >
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Date + Time */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-white/60 mb-1 flex items-center gap-1">
                        <CalendarBlank size={12} /> {t("newBooking.date")}
                        {r.trip_date && (
                          <span className="text-primary text-[11px] mr-auto">{arWeekday(r.trip_date)}</span>
                        )}
                      </label>
                      <CustomDatePicker
                        value={r.trip_date}
                        onChange={(v) => updateRoute(i, { trip_date: v })}
                        placeholder="YYYY-MM-DD"
                        testId={`route-date-${i}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white/60 mb-1 flex items-center gap-1">
                        <Clock size={12} /> {t("newBooking.time")}
                      </label>
                      <ModalTimePicker
                        value={r.trip_time}
                        onChange={(v) => updateRoute(i, { trip_time: v })}
                        placeholder="HH:mm"
                        testId={`route-time-${i}`}
                        minuteStep={5}
                      />
                    </div>
                  </div>

                  {/* Pickup FIRST (per user requirement — top), Destination SECOND (bottom) */}
                  <div className="mb-3">
                    <LocationPicker
                      label={t("newBooking.pickupLocation")}
                      value={r.pickup_location}
                      onChange={(v) => updateRoute(i, { pickup_location: v })}
                      includeShrines={false}
                      otherEndpointValue={r.destination}
                      testIdPrefix={`route-pickup-${i}`}
                    />
                  </div>
                  <div className="mb-3">
                    <LocationPicker
                      label={t("newBooking.destination")}
                      value={r.destination}
                      onChange={(v) => updateRoute(i, { destination: v })}
                      includeShrines={true}
                      pickupContext={r.pickup_location}
                      otherEndpointValue={r.pickup_location}
                      testIdPrefix={`route-destination-${i}`}
                    />
                  </div>

                  {/* Flight (optional) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-white/60 mb-1 flex items-center gap-1">
                        <Airplane size={12} /> {t("newBooking.flightNumber")}
                      </label>
                      <input
                        type="text"
                        value={r.flight_number}
                        onChange={(e) => updateRoute(i, { flight_number: e.target.value })}
                        className="mz-input num"
                        dir="ltr"
                        data-testid={`route-flight-num-${i}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white/60 mb-1">{t("newBooking.flightAirline")}</label>
                      <input
                        type="text"
                        value={r.flight_airline}
                        onChange={(e) => updateRoute(i, { flight_airline: e.target.value })}
                        className="mz-input"
                        data-testid={`route-flight-airline-${i}`}
                      />
                    </div>
                  </div>

                  {/* Per-route ticket image */}
                  <div className="mb-3">
                    <div className="text-white/80 text-xs font-bold mb-2">{t("newBooking.ticketImage")}</div>
                    {r.ticket_image ? (
                      <div className="flex items-center gap-3">
                        <img src={r.ticket_image} alt="ticket" className="w-20 h-20 rounded-lg object-cover border border-primary/25" />
                        <div className="flex flex-col gap-2">
                          <label className="inline-flex items-center gap-1 text-primary text-xs cursor-pointer hover:underline">
                            <UploadSimple size={12} /> {t("newBooking.replace")}
                            <input type="file" accept="image/*" hidden onChange={(e) => onTicketChange(i, e)} data-testid={`ticket-replace-${i}`} />
                          </label>
                          <button
                            type="button"
                            onClick={() => updateRoute(i, { ticket_image: null })}
                            className="inline-flex items-center gap-1 text-destructive text-xs hover:underline"
                            data-testid={`ticket-remove-${i}`}
                          >
                            <X size={12} /> {t("newBooking.remove")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="block w-full cursor-pointer rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 p-4 text-center transition-all">
                        <UploadSimple size={20} className="text-primary mx-auto mb-1" />
                        <div className="text-primary text-xs font-semibold">{t("newBooking.uploadHint")}</div>
                        <input type="file" accept="image/*" hidden onChange={(e) => onTicketChange(i, e)} data-testid={`ticket-upload-${i}`} />
                      </label>
                    )}
                  </div>

                  {/* Cars */}
                  <div className="pt-3 border-t border-primary/10">
                    <div className="text-white font-bold text-sm mb-2">{t("newBooking.carsHint")}</div>
                    <CarPicker
                      routeIndex={i}
                      route={r}
                      onQtyChange={(car_type, qty) =>
                        updateRoute(i, {
                          cars: {
                            ...r.cars,
                            [car_type]: qty,
                          },
                        })
                      }
                      prices={prices}
                      loading={!!pricesLoading[i]}
                      fallbackCars={fleetCars}
                      t={t}
                    />
                  </div>

                  {/* Per-route total */}
                  {routeTotal > 0 && (
                    <div className="mt-3 pt-3 border-t border-primary/10 flex items-center justify-between">
                      <span className="text-white/60 text-sm">{t("newBooking.routePrice")}</span>
                      <span className="text-primary font-bold text-lg num" data-testid={`route-total-${i}`}>
                        {fmtRiyal(routeTotal)}
                      </span>
                    </div>
                  )}
                </SectionCard>
              );
            })}

            {/* 4) Passengers — always visible, standalone, mandatory */}
            <SectionCard data-testid="section-passengers">
              <div className="flex items-center gap-2 mb-3">
                <Users size={18} weight="duotone" className="text-primary" />
                <h2 className="text-white font-bold text-lg">{t("newBooking.paxSection")}</h2>
              </div>
              <NumberField
                value={passengers}
                onChange={setPassengers}
                min={1}
                className="mz-input num text-lg"
                required
                testId="passengers"
                placeholder="0"
              />
              {totalCapacity > 0 && (
                <div className="mt-2 text-primary/70 text-xs num">
                  {t("newBooking.capacityHint", { count: fmtNum(totalCapacity) })}
                </div>
              )}
            </SectionCard>

            {/* 4b) Notes — optional */}
            <SectionCard data-testid="section-notes">
              <div className="text-white/80 text-sm font-bold mb-2">{t("newBooking.notes")}</div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="mz-input"
                data-testid="notes"
              />
            </SectionCard>

            {/* 5) Grand total (moved BEFORE add-route per UX request) */}
            <div
              data-testid="quote-box"
              className="rounded-2xl border-2 border-primary bg-gradient-to-br from-primary/15 via-primary/5 to-transparent p-5"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/80 text-sm font-semibold">{t("newBooking.totalLabel")}</span>
                {quoting && <span className="text-primary/70 text-xs animate-pulse">{t("newBooking.calculating")}</span>}
              </div>
              {quote && quote.routes && quote.routes.length > 0 ? (
                <>
                  <div className="space-y-1.5 text-sm">
                    {quote.routes.map((qr, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between text-white/80"
                        data-testid={`quote-row-${idx}`}
                      >
                        <span className="truncate max-w-[70%]">
                          {t("newBooking.route")} {fmtNum(idx + 1)}: {qr.pickup_location} → {qr.destination}
                        </span>
                        <span className="num text-primary/90 font-semibold">{fmtRiyal(qr.route_total || 0)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-primary/30 flex items-center justify-between">
                    <span className="text-white text-base font-bold">{t("newBooking.totalGrand")}</span>
                    <span
                      className="text-3xl md:text-4xl font-bold text-primary num"
                      data-testid="quote-total"
                    >
                      {fmtRiyal(quote.total_price || 0)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-white/60 text-sm text-center py-2">
                  {t("newBooking.totalPrompt")}
                </div>
              )}
            </div>

            {/* 6) Add route (moved AFTER quote per UX request) */}
            <button
              type="button"
              onClick={addRoute}
              data-testid="btn-add-route"
              className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-primary/60 bg-primary/5 hover:bg-primary/15 text-primary font-bold py-3.5 transition-all"
            >
              <Plus size={16} weight="bold" /> {t("newBooking.addRoute", { n: fmtNum(routes.length + 1) })}
            </button>

            {/* 8) Submit */}
            <button
              type="submit"
              disabled={saving || totalCarsCount === 0}
              data-testid="btn-submit-booking"
              className="w-full py-4 rounded-full bg-primary text-primary-foreground font-bold text-lg hover:bg-primary/90 shadow-[0_10px_30px_-10px_rgba(212,175,55,0.6)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t("newBooking.submitting") : t("newBooking.submit")}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
